import { Button, Icon } from '@veraclins-dev/ui';
import { cn } from '@veraclins-dev/utils';
import { useSyncExternalStore } from 'react';

import { useRequestInfo } from '../request-info';
import { setClientThemeMode, useTheme, useThemeMode } from '../theme';
import { type Theme } from '../types';

const emptySubscribe = () => () => undefined;
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

function useHydrated() {
  return useSyncExternalStore(
    emptySubscribe,
    getClientSnapshot,
    getServerSnapshot,
  );
}

function getNextMode(currentMode: Theme | 'system') {
  return currentMode === 'system'
    ? 'light'
    : currentMode === 'light'
      ? 'dark'
      : 'system';
}

function getIcon(currentMode: Theme | 'system', resolvedTheme: Theme) {
  const theme = currentMode === 'system' ? resolvedTheme : currentMode;
  return theme === 'dark' ? 'moon' : 'sun';
}

function getLabel(nextMode: Theme | 'system') {
  if (nextMode === 'system') {
    return 'Switch to system theme';
  }
  return nextMode === 'dark' ? 'Switch to dark mode' : 'Switch to light mode';
}

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const isHydrated = useHydrated();
  const { hints, userPrefs } = useRequestInfo();
  const serverMode = userPrefs.theme ?? 'system';
  const resolvedTheme = userPrefs.theme ?? hints.theme;

  if (!isHydrated) {
    const nextMode = getNextMode(serverMode);
    const label = getLabel(nextMode);

    return (
      <Button
        type="button"
        emphasis="ghost"
        buttonSize="icon"
        aria-label={label}
        tooltip={label}
        p={1}
        className={cn('transition-none', className)}
        noRing
        data-testid="theme-toggle"
        disabled
      >
        <Icon name={getIcon(serverMode, resolvedTheme)} size="sm" />
      </Button>
    );
  }

  return <MountedThemeToggle className={className} />;
}

function MountedThemeToggle({ className }: ThemeToggleProps) {
  const currentMode = useThemeMode();
  const resolvedTheme = useTheme();
  const nextMode = getNextMode(currentMode);
  const label = getLabel(nextMode);

  return (
    <Button
      type="button"
      emphasis="ghost"
      buttonSize="icon"
      aria-label={label}
      tooltip={label}
      p={1}
      className={cn('transition-none', className)}
      noRing
      data-testid="theme-toggle"
      onClick={() => setClientThemeMode(nextMode)}
    >
      <Icon name={getIcon(currentMode, resolvedTheme)} size="sm" />
    </Button>
  );
}
